import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api, formatApiError, RunDetail as RunDetailType } from '../api/client'
import { fmtTime } from '../utils/datetime'
import { legLabel, outboundMethodLabel, transportForMethodValue, transportLabel, providerLabel, PROVIDER_NOT_RECORDED } from '../utils/methods'
import { outboundHandleLabel } from '../utils/outboundHandle'
// The ONE decision about whether this run's `sending` needs qualifying — shared with the
// dashboard listing and the Info panel rather than re-derived per surface.
import { describeOutboundSettlement as settlementMark } from '../utils/runStatus'
import { useIsAdmin } from '../auth/AuthContext'
import { theme } from '../theme'
import { QualityScorePanel } from '../components/QualityScorePanel'
import { DidAttributionPanel } from '../components/DidAttributionPanel'
import { Hint, TimestampRow } from '../components/Hint'
import { TransmissionMeasurements } from '../components/TransmissionMeasurements'

export default function RunDetail() {
  const isAdmin = useIsAdmin()
  const { id } = useParams<{ id: string }>()
  const [run, setRun] = useState<RunDetailType | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [resolveNote, setResolveNote] = useState('')
  const [showResolve, setShowResolve] = useState(false)

  // .catch is not optional here. Without it a failed fetch rejects silently, setRun
  // is never called, and the page sits on "Loading..." forever — which is how a
  // backend 500 presented as a blank page with no clue, both in the UI and to the
  // person reporting it. An error state costs three lines and turns "it's broken"
  // into "here is the status code".
  useEffect(() => {
    if (!id) return
    setLoadError(null)
    api.getRunDetail(id)
      .then(setRun)
      .catch(err => setLoadError(formatApiError(err, 'Could not load this run')))
  }, [id])

  const handleResolve = async () => {
    if (!id || !resolveNote) return
    await api.resolveRun(id, resolveNote)
    api.getRunDetail(id).then(setRun).catch(err =>
      setLoadError(formatApiError(err, 'Could not reload this run')))
    setShowResolve(false)
  }

  if (loadError) {
    return (
      <div style={{ maxWidth: '700px' }}>
        <h2 style={{ marginTop: 0 }}>Run Detail</h2>
        <div style={{ background: '#3a1e1e', border: '1px solid #6a2a2a', borderRadius: '6px', padding: '1rem' }}>
          <p style={{ margin: 0, color: '#ef9a9a' }}>{loadError}</p>
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#9aa0a6' }}>
            Run ID: <code>{id}</code>
          </p>
        </div>
      </div>
    )
  }

  if (!run) return <div>Loading...</div>

  const canResolve = ['in_flight', 'held', 'sending'].includes(run.status)

  return (
    <div style={{ maxWidth: '900px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem' }}>
        {/* THE JOB NAME IS THE HEADING; THE TOKEN STAYS BESIDE IT.
            The token was the only title here, which answered "which fax" and never "which
            job" — the question an operator opening a run from a Slack link or a batch report
            actually arrives with. It is NOT replaced: it is what a run is searched by on the
            history pages and what gets pasted into a provider ticket, so it stays rendered,
            monospace and selectable.

            The name links to the job rather than to its run history: this page IS a run, so
            the useful next hop is the configuration that produced it. `job_name` is null only
            when the job row could not be resolved — that reads as "Run", never as a guessed
            title, and the link is withheld with it. */}
        <h2 style={{ margin: 0, fontSize: '1.15rem', display: 'flex', alignItems: 'baseline', gap: '0.6rem', flexWrap: 'wrap' }}>
          {run.job_name
            ? (
              <Link to={`/jobs/${run.job_id}`} style={{ color: theme.primary, textDecoration: 'none' }}>
                {run.job_name}
              </Link>
            )
            : <span>Run</span>}
          <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 400, color: '#9aa0a6', userSelect: 'all' }}>
            {run.run_token}
          </span>
        </h2>
        {canResolve && isAdmin && (
          <button onClick={() => setShowResolve(true)} style={{ padding: '0.5rem 1rem', background: '#f44336', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', flexShrink: 0 }}>
            Mark Failed
          </button>
        )}
      </div>

      {showResolve && (
        <div style={{ background: '#3a2e18', padding: '1rem', borderRadius: '4px', marginBottom: '1rem', border: '1px solid #5a4a24' }}>
          <textarea value={resolveNote} onChange={e => setResolveNote(e.target.value)} placeholder="Explain why this run is being manually failed..." rows={2} style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', background: '#1e1e1e', color: '#f5f5f5', border: '1px solid #333', borderRadius: '4px' }} />
          <button onClick={handleResolve} disabled={!resolveNote} style={{ padding: '0.4rem 1rem', background: '#f44336', color: '#fff', border: 'none', borderRadius: '4px' }}>Confirm</button>
          <button onClick={() => setShowResolve(false)} style={{ marginLeft: '0.5rem', padding: '0.4rem 1rem', border: '1px solid #555', background: '#1e1e1e', color: '#f5f5f5', borderRadius: '4px' }}>Cancel</button>
        </div>
      )}

      {/* Status & Timing */}
      <Section title="Status & Timing">
        <Row label="Status" value={run.status} />
        {/* Which send path this run took, QUALIFIED BY THE PROVIDER THAT CARRIED IT.
            Reported from production: an SRFax API run read "eAPI" — eFax's brand name for its
            REST API — because this row called `outboundMethodLabel`, which maps the stored
            method value with no provider in scope at all. The per-provider table already
            existed and this call site bypassed it: `legLabel` resolves 'srfax' + 'api' on the
            OUTBOUND leg to "SRFax API (Queue_Fax)" and 'efax' + 'eapi' to "eFax eAPI", so the
            fix is to ask it rather than to add another table.

            THE ROW'S OWN PROVIDER, not the job's. A job re-pointed after this fax went out
            would otherwise relabel a historical transmission with a provider that never
            carried it. `outbound_provider` NULL means NOT RECORDED, and `legLabel` says so
            rather than naming eFax — but the transport is still worth showing for such a row,
            so the bare method label remains the fallback. */}
        <Row
          label="Outbound Method"
          value={
            run.outbound_provider
              ? legLabel(
                  run.outbound_provider,
                  run.outbound_transport || transportForMethodValue(run.outbound_method),
                  'outbound',
                )
              : outboundMethodLabel(run.outbound_method)
          }
        />
        {/* `outbound_status` is written by EVERY sender — SFAX_STATUS_ACCEPTED and
            SRFAX_STATUS_ACCEPTED land in this same column — so heading it "(eFax)" told an
            operator an SFax status word came from eFax. The value is unbranded because the
            row's own provider is already named in the Outbound Method row above. */}
        {run.outbound_status && <Row label="Outbound Status" value={run.outbound_status} />}
        {/* THE INBOUND LEG, on a page whose whole subject is a TWO-LEG transmission.
            There was an Outbound Method row and no counterpart, while `inbound_provider` and
            `inbound_transport` were served on the payload and read by nothing — so the half of
            the measurement that closes the run was invisible, and a MIXED-PROVIDER job (send
            over one vendor, receive over another; supported, and the job editor says so) could
            not be told apart from a single-vendor one on the record of the transmission.

            No method fallback, unlike the outbound row above: the run schema carries
            `outbound_method` and no inbound equivalent, so the transport column IS the only
            statement of how the fax came back. NULL provider reads as NOT RECORDED — the
            transport is still worth showing for such a row, and when neither was recorded the
            row says that rather than guessing a leg the transmission may never have had. */}
        <Row
          label="Inbound Method"
          value={
            run.inbound_provider
              ? legLabel(run.inbound_provider, run.inbound_transport, 'inbound')
              : run.inbound_transport
                ? `${transportLabel(null, run.inbound_transport, 'inbound')} (provider ${PROVIDER_NOT_RECORDED})`
                : PROVIDER_NOT_RECORDED
          }
        />
        {/* WHETHER `sending` IS THIS LEG'S RESTING STATE, AND WHAT WILL ACTUALLY CLOSE IT.
            Shown only while the run IS in `sending`, and only when the leg cannot settle
            itself — `describeOutboundSettlement` decides both, from the backend's
            `outbound_self_settling`, so this page, the dashboard listing and the Info panel
            cannot disagree about which rows are qualified.

            THE FULL PARAGRAPH RATHER THAN THE BADGE, because a page has room for it and
            because the badge alone is the half-fix: "no completion feedback" tells a reader
            the row is different and leaves them to guess whether that is bad. The sentence
            says no action is required and names what will move it — the inbound leg arriving,
            or the inbound SLA elapsing and failing it as receive_timeout. Both come from
            `services/outbound_settlement.py`; nothing here re-derives them from a provider
            name, which is how the UI grew two per-provider chains it then had to keep in step
            with the backend by hand. */}
        {settlementMark(run).mark && (
          <div
            style={{
              gridColumn: '1 / -1', margin: '0.4rem 0 0.2rem', padding: '0.6rem 0.75rem',
              background: theme.bgElevated, border: `1px solid ${theme.border}`,
              borderLeft: '3px solid #ffa726', borderRadius: 6,
              fontSize: '0.8rem', color: theme.text, lineHeight: 1.45,
            }}
          >
            <strong style={{ color: '#ffa726' }}>{settlementMark(run).badge}</strong>
            <div style={{ marginTop: '0.3rem', color: '#c8ccd0' }}>
              {run.outbound_settlement_detail || settlementMark(run).text}
            </div>
          </div>
        )}
        <Row label="SLA Breached" value={run.sla_breached ? '⚠️ Yes' : 'No'} />
        <Row label="Outbound SLA" value={legSla(run.outbound_sla_breached, run.outbound_sla_seconds, run.t_fax_sent, run.sla_basis, run.combined_sla_seconds, run.outbound_sla_minutes_effective)} />
        <Row label="Inbound SLA" value={legSla(run.inbound_sla_breached, run.inbound_sla_seconds, run.t_received, run.sla_basis, run.combined_sla_seconds, run.inbound_sla_minutes_effective)} />
        {/* Every timestamp below carries its provenance, and both detail pages render them
            through the SAME component with the SAME labels (G5.10-G5.15). The two pages used
            to disagree — "Fax Sent (eFax completed)" here against "Platform confirmed sent"
            on batch item detail — and the first was actively wrong: it named an eFax clock
            for a value that falls back to OURS when the provider reports no completion time.
            The label no longer claims whose clock it is; the description says, per row. */}
        <TimestampRow layout="row" column="t_scheduled" value={run.t_scheduled} row={run} />
        <TimestampRow
          layout="row"
          column="t_outbound_request_started"
          value={run.t_outbound_request_started}
          row={run}
        />
        <TimestampRow layout="row" column="t_submitted" value={run.t_submitted} row={run} />
        <TimestampRow layout="row" column="t_fax_sent" value={run.t_fax_sent} row={run} />
        <TimestampRow layout="row" column="t_received" value={run.t_received} row={run} />
        {run.smtp_notification_received_at && (
          <TimestampRow
            layout="row"
            column="smtp_notification_received_at"
            value={run.smtp_notification_received_at}
            row={run}
          />
        )}
        <TimestampRow
          layout="row"
          column="t_quality_checked"
          value={run.t_quality_checked}
          row={run}
        />
        <Row label="E2E Duration" value={run.e2e_duration_seconds ? `${run.e2e_duration_seconds.toFixed(1)} seconds` : '—'} />
        {/* Labelled from the row's OWN provider, never from a literal: this column is the
            provider-agnostic outbound handle (SFax's SendFaxQueueId, SRFax's FaxDetailsID),
            and "eFax fax_id" over an SRFax id is the defect utils/outboundHandle exists to
            stop. NULL provider is reported as not recorded, not filled in with a brand. */}
        {run.efax_fax_id && (
          <Row label={outboundHandleLabel(run.outbound_provider)} value={run.efax_fax_id} />
        )}
      </Section>

      {/* Measurements this project has always captured and never displayed: the four
          inbound clocks, pod attribution for both legs, eFax's airtime and the sent page
          count — plus a proportional bar so "where did the time go" does not require
          subtracting six timestamps by hand.

          Rendered unconditionally, unlike most sections here. When nothing was captured
          the panel says so in words; hiding it instead would leave an operator unable to
          tell "this run has no measurements" from "this build has no such panel", and the
          second reading is what gets filed as a bug. The panel is shared with
          BatchItemDetail so the two pages cannot drift. */}
      <Section title="Transmission Measurements">
        <TransmissionMeasurements source={run} />
      </Section>

      {/* Objective transmission quality, measured against the calibration target.
          Kept in its OWN section, separate from the Quality Check below: that one
          answers "did the fax arrive intact?" and this answers "how good was the
          transmission?". Merging them would lose one of the two questions. */}
      {/* WHICH DID THIS FAX ARRIVED FOR, AND ON WHAT AUTHORITY (DID-AUTHORITY-LADDER rule 1).
          Rendered unconditionally, like the measurements panel above and for the same reason: a
          hidden panel leaves a reader unable to tell "nothing was recorded for this fax" from
          "this build has no such panel", and the second reading gets filed as a bug. The panel is
          SHARED with the other detail surface so the two pages cannot describe one row's
          authority differently. */}
      <Section title="Arrival DID attribution">
        <DidAttributionPanel row={run} />
      </Section>

      <Section title="Transmission Quality Score">
        <QualityScorePanel
          score={run.quality_score}
          detail={run.quality_score_detail}
          runId={id}
          canRescore={isAdmin}
          onRescored={() => {
            // Re-fetch rather than patching local state: the backend decided both the
            // score and whether a target was found, and guessing either here would let
            // the panel disagree with the record.
            fetch(`/api/runs/${id}`)
              .then(r => r.json())
              .then(setRun)
              .catch(e => setLoadError(String(e)))
          }}
        />
      </Section>

      {/* Quality */}
      <Section title="Quality Check">
        <Row label="Result" value={run.quality_result || '—'} />
        <Row label="Detail" value={run.quality_detail || '—'} />
        <Row label="OCR-extracted Run ID" value={run.ocr_extracted_token || '—'} />
        {run.received_page_count && <Row label="Received Pages" value={String(run.received_page_count)} />}
      </Section>

      {/* THE PROVIDER STATUS HISTORY, WITH THE DIAGNOSTIC FIELDS IT ACTUALLY HOLDS.
          Two defects in three lines, both silent. It was headed "eAPI Status History" — eFax's
          brand for its REST API — over a column every provider appends to: an SFax status poll
          writes `result_code` / `error_code` / `result_message`, an SRFax poll writes
          `error_code` / `remote_id` / `duration_seconds`, and the two outbound CALLBACKS append
          entries with no `status` word at all and a `at` key instead of `timestamp`. So the
          heading named the wrong vendor and the render threw away every field except two —
          then drew a blank row for each callback entry, because it read the two keys those
          entries do not have.

          Each entry names ITS OWN provider (the writers stamp it), not the row's: an entry is
          appended by whichever leg reported, and a job re-pointed later must not relabel a
          historical entry. An entry with no recorded provider reads as NOT RECORDED — the
          pre-provider-stamp entries are exactly that and must not be filled in with eFax. */}
      {run.api_status_history && run.api_status_history.length > 0 && (
        <Section title="Provider Status History">
          {run.api_status_history.map((entry, i) => (
            <StatusHistoryEntry key={i} entry={entry as unknown as Record<string, unknown>} />
          ))}
        </Section>
      )}

      {/* OCR Text */}
      {run.ocr_text && (
        <Section title="OCR Text (first page)">
          <pre style={{ fontSize: '0.75rem', background: '#262626', color: '#f5f5f5', padding: '0.75rem', borderRadius: '4px', whiteSpace: 'pre-wrap', maxHeight: '200px', overflow: 'auto' }}>
            {run.ocr_text}
          </pre>
        </Section>
      )}

      {/* Stored inbound fax payload. Only shown when something was actually
          stored for this run — a run that never received a fax has no payload
          to talk about, deleted or otherwise. */}
      {run.received_document_available !== null && run.received_document_available !== undefined && (
        <Section title="Received Fax Payload">
          {run.received_document_available ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <a
                href={`/api/runs/${run.id}/received-image`}
                style={{
                  display: 'inline-block', padding: '0.4rem 0.8rem', fontSize: '0.85rem',
                  background: theme.bgElevated, color: theme.text,
                  border: `1px solid ${theme.border}`, borderRadius: 4, textDecoration: 'none',
                }}
              >
                ⬇ Download received fax
              </a>
              <span style={{ fontSize: '0.78rem', color: theme.textMuted }}>
                {run.received_page_count ? `${run.received_page_count} page${run.received_page_count === 1 ? '' : 's'}` : null}
                {run.received_page_count && run.received_file_size_bytes ? ' · ' : null}
                {run.received_file_size_bytes ? `${(run.received_file_size_bytes / 1024).toFixed(0)} KB` : null}
                {run.inbound_retention_days ? ` · retained ${run.inbound_retention_days} days` : null}
              </span>
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: '0.85rem', color: theme.textMuted }}>
              <strong style={{ color: theme.text }}>Fax Payload Deleted</strong>
              {run.inbound_retention_days
                ? ` — stored faxes are retained for ${run.inbound_retention_days} days, and this run's payload has passed that window.`
                : ' — the stored payload is no longer present.'}
            </p>
          )}
        </Section>
      )}

      {/* THE INBOUND WEBHOOK PAYLOAD, HEADED BY THE PROVIDER THAT SENT IT.
          "eAPI Webhook Payload" is eFax's own brand for its webhook, and this column holds an
          SFax callback record just as readily — so an SFax delivery had its raw evidence filed
          under eFax's name. Resolved from the row's INBOUND leg (the leg a delivery webhook
          belongs to) through `legLabel`, which answers "SFax API Webhook" or "eFax eAPI
          Webhook" and reports NOT RECORDED rather than naming a brand for a NULL provider. */}
      {run.webhook_payload_body && (
        <Section
          title={
            run.inbound_provider
              ? `${legLabel(run.inbound_provider, run.inbound_transport || 'webhook', 'inbound')} Payload (Raw)`
              : `Inbound Webhook Payload (Raw) — provider ${PROVIDER_NOT_RECORDED}`
          }
        >
          <pre style={{ fontSize: '0.75rem', background: '#262626', color: '#f5f5f5', padding: '0.75rem', borderRadius: '4px', whiteSpace: 'pre-wrap', maxHeight: '300px', overflow: 'auto' }}>
            {JSON.stringify(run.webhook_payload_body, null, 2)}
          </pre>
          {run.webhook_payload_headers && (
            <details style={{ marginTop: '0.5rem' }}>
              <summary style={{ cursor: 'pointer', fontSize: '0.8rem', color: '#9aa0a6' }}>Headers</summary>
              <pre style={{ fontSize: '0.7rem', background: '#262626', color: '#f5f5f5', padding: '0.5rem', borderRadius: '4px' }}>
                {JSON.stringify(run.webhook_payload_headers, null, 2)}
              </pre>
            </details>
          )}
        </Section>
      )}

      {/* IMAP Metadata */}
      {run.imap_headers && (
        <Section title="IMAP Message">
          <Row label="Message-ID" value={run.imap_message_id || '—'} />
          {Object.entries(run.imap_headers).map(([k, v]) => (
            <Row key={k} label={k} value={v} />
          ))}
        </Section>
      )}

      {/* Manual Resolution */}
      {run.manually_failed_at && (
        <Section title="Manual Resolution">
          <TimestampRow
            layout="row"
            column="manually_failed_at"
            value={run.manually_failed_at}
            row={run}
          />
          <Row label="Failed By" value={run.manually_failed_by || '—'} />
          <Row label="Note" value={run.manually_failed_note || '—'} />
        </Section>
      )}
    </div>
  )
}

/** Keys the header line of a status-history entry already renders. */
const HISTORY_HEADER_KEYS = new Set(['status', 'timestamp', 'at', 'provider', 'event'])

/** A scalar as an operator would read it; a nested object verbatim, because it is evidence. */
function historyValue(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

/** The first of `keys` this entry holds as a non-empty string. */
function historyString(entry: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = entry[key]
    if (typeof value === 'string' && value.trim()) return value
  }
  return null
}

/**
 * One entry of `api_status_history`, WITH the diagnostic fields its writer recorded.
 *
 * The previous render was `<Row label={entry.status} value={fmtTime(entry.timestamp)} />`, which
 * discarded everything else in the entry — `result_code`, `error_code`, `result_message`,
 * `remote_id`, `duration_seconds`, `pages`, the raw provider status word and both unparsed date
 * strings — every one of them already in the database and every one of them reachable only by
 * digging through logs. `ErrorCode` paired with `RemoteID` is what an SRFax support ticket is
 * opened on; `ResultCode` paired with `ErrorCode` is SFax's.
 *
 * THE FIELDS ARE DISCOVERED, NOT LISTED. Rendering the keys the writer actually put in the
 * entry means a provider that starts reporting one more field surfaces it with no edit here —
 * and it is why nothing had to be named to fix five losses at once. A listed set would go stale
 * exactly as silently as the two-key render did.
 *
 * BOTH TIMESTAMP SPELLINGS. The status polls write `timestamp` and the two outbound callbacks
 * (SFax's and SRFax's) write `at` and carry no `status` word at all, so the old render drew a
 * blank label and an em dash for those entries — an entry that DID arrive, presented as an
 * empty row.
 */
function StatusHistoryEntry({ entry }: { entry: Record<string, unknown> }) {
  const when = historyString(entry, 'timestamp', 'at')
  const status = historyString(entry, 'status')
  const event = historyString(entry, 'event')
  const provider = historyString(entry, 'provider')
  const details = Object.entries(entry).filter(
    ([key, value]) =>
      !HISTORY_HEADER_KEYS.has(key) && value !== null && value !== undefined && value !== '',
  )

  return (
    <div style={{ padding: '0.35rem 0', borderBottom: '1px solid #2a2a2a' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
        <span style={{ fontSize: '0.8rem' }}>
          {/* An entry with no status word is labelled by the EVENT that produced it rather than
              by a blank: a callback arriving is a fact, and the absence of a status word on it
              is a property of that path, not missing data. */}
          {status || event || 'entry'}
          <span style={{ marginLeft: '0.45rem', fontSize: '0.72rem', color: '#9aa0a6' }}>
            {providerLabel(provider)}
            {status && event ? ` · ${event}` : ''}
          </span>
        </span>
        <span style={{ fontSize: '0.8rem', color: '#9aa0a6' }}>{when ? fmtTime(when) : '—'}</span>
      </div>
      {details.length > 0 && (
        <div style={{ marginTop: '0.25rem', paddingLeft: '0.6rem' }}>
          {details.map(([key, value]) => (
            <div
              key={key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '0.75rem',
                fontSize: '0.72rem',
              }}
            >
              <span style={{ color: '#9aa0a6', fontFamily: 'monospace' }}>{key}</span>
              <span
                style={{
                  maxWidth: '70%',
                  textAlign: 'right',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                }}
              >
                {historyValue(value)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {  return (
    <div style={{ background: '#1e1e1e', borderRadius: '8px', padding: '1.2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.4)', border: '1px solid #333', marginBottom: '1rem' }}>
      <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: '#9aa0a6' }}>{title}</h3>
      {children}
    </div>
  )
}

/**
 * One label / value line.
 *
 * `hint` is optional and, when given, the line is rendered by the shared `Hint` component
 * rather than by a second copy of the affordance here — that is the whole point of G5.16.
 * The row styling below and `Hint`'s `layout="row"` are deliberately identical, so a row
 * with a description does not jump relative to one without.
 */
function Row({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  if (hint) return <Hint layout="row" label={label} value={value} hint={hint} />
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: '1px solid #2a2a2a' }}>
      <span style={{ color: '#9aa0a6', fontSize: '0.8rem' }}>{label}</span>
      <span style={{ fontSize: '0.8rem', maxWidth: '60%', textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
    </div>
  )
}

function legSla(
  breached: boolean | null | undefined,
  seconds: number | null | undefined,
  ts: string | undefined,
  basis?: string,
  combinedSeconds?: number | null,
  thresholdMinutes?: number | null,
): string {
  // NO THRESHOLD on this leg -> the leg is NOT EVALUATED, and the measured time is
  // still shown.
  //
  // This must be checked FIRST, before the pending branch, because both states
  // arrive as a null verdict and they are not the same fact: "pending" resolves
  // eventually, "not evaluated" never will. Rendering a permanent "— (pending)" for a
  // leg nobody set a threshold on would be a status that lies by implying it is still
  // waiting on something.
  //
  // Worded "not evaluated", not "no SLA": there is no job-wide fallback behind a blank
  // leg any more, and a blank leg is not a pass.
  //
  // thresholdMinutes === undefined means the API did not supply it (older payload),
  // which is NOT the same as null (explicitly no threshold) — so only null triggers it.
  if (thresholdMinutes === null) {
    const dur = typeof seconds === 'number' ? ` (${(seconds / 60).toFixed(1)} min)` : ''
    return `N/A — not evaluated${dur}`
  }
  if (breached === null || breached === undefined) {
    return ts ? '—' : '— (pending)'
  }
  const verdict = breached ? '⚠️ Breach' : '✓ Pass'
  // t_fax_sent never arrived, so there is no boundary between the legs and neither leg
  // could be measured. Only the submit → received DURATION exists.
  //
  // This used to read "✓ Pass (N min combined)" — a verdict against a combined
  // THRESHOLD, which no longer exists: thresholds are per leg. An unmeasurable leg is
  // not evaluated, and not evaluated is not a pass, so the absence of a breach is not
  // promoted to one here. A reported breach is still surfaced.
  if (basis === 'combined') {
    const mins = typeof combinedSeconds === 'number' ? (combinedSeconds / 60).toFixed(1) : '?'
    const window = `${mins} min submit→received; no leg boundary`
    return breached ? `⚠️ Breach (${window})` : `Not evaluated (${window})`
  }
  // NOTE: must be `!= null`, not `!== undefined`. The backend sends JSON null for
  // an unmeasurable leg, and `null !== undefined` is true while `null / 60` is 0,
  // which previously rendered a confident "0.0 min" for runs with no t_fax_sent.
  const dur = typeof seconds === 'number' ? ` (${(seconds / 60).toFixed(1)} min)` : ''
  return verdict + dur
}
